import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CopyIcon, ShareIcon } from '@streamr/tv-ui';
import { executeSoon } from '@streamr/tv-utils';
import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { StreamrAppService } from '../../services/streamrapp/StreamrAppService';
import { Popup, TopCenter } from '../../utils/popups';
import { Button } from '../Button';
import { AbstractPopup, DismissalReason } from './AbstractPopup';
import { ScrollablePopupContainer } from './helpers/ScrollablePopupContainer';
import { PopupCssPreset } from './Popup.styles';
function CameraInvitePopup({ anchorRef, reject }) {
    const [cameraUrl, setCameraUrl] = useState();
    useEffect(() => {
        StreamrAppService.getInstance()
            .notifyCreateCameraInviteUrl()
            .then(setCameraUrl)
            .catch(console.error);
    }, []);
    const handleCopy = useCallback(async () => {
        if (!cameraUrl)
            return;
        try {
            await navigator.clipboard.writeText(cameraUrl);
        }
        catch {
            // clipboard API may not be available
        }
    }, [cameraUrl]);
    const handleShare = useCallback(async () => {
        if (!cameraUrl || !navigator.share)
            return;
        try {
            await navigator.share({
                title: 'Camera link',
                url: cameraUrl,
            });
        }
        catch {
            // user cancelled share
        }
    }, [cameraUrl]);
    return (_jsx(CameraInvitePopupRoot, { reject: reject, anchorRef: anchorRef, translateFn: TopCenter, children: _jsx(ScrollablePopupContainer, { onCloseButtonClick: () => {
                reject(DismissalReason.CloseButton);
            }, children: _jsxs(Content, { children: [_jsx(Title, { children: "Camera invite" }), _jsx(Subtitle, { children: "Share this link with a camera operator. They will be able to stream video and audio to your session." }), _jsx(UrlDisplay, { children: cameraUrl ?? 'Generating camera link...' }), _jsxs(ButtonRow, { children: [_jsxs(ActionButton, { type: "button", kind: "secondary", onClick: handleCopy, disabled: !cameraUrl, children: [_jsx(CopyIcon, {}), "Copy link"] }), typeof navigator.share === 'function' && (_jsxs(ActionButton, { type: "button", kind: "secondary", onClick: handleShare, disabled: !cameraUrl, children: [_jsx(ShareIcon, {}), "Share"] }))] }), _jsx(DoneButton, { type: "button", onClick: () => {
                            reject(DismissalReason.CloseButton);
                        }, children: "Done" })] }) }) }));
}
const CameraInvitePopupRoot = styled(AbstractPopup) `
    ${PopupCssPreset.Fullscreen};
    --Popup_Padding: 0;

    @media (min-width: 30rem) {
        ${PopupCssPreset.AnchoredTopCenter};
        --Popup_Padding: 1rem 0;
        --Popup_Width: 25rem;
    }
`;
const Content = styled.div `
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem;
`;
const Title = styled.h2 `
    font-size: 1.375rem;
    font-weight: 600;
    line-height: 1.5em;
    color: #ffffff;
    margin: 0;
`;
const Subtitle = styled.p `
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: rgba(235, 235, 245, 0.6);
    margin: 0 0 0.5rem;
    text-align: center;
`;
const UrlDisplay = styled.div `
    width: 100%;
    padding: 0.75rem;
    background-color: var(--tv-ui-color-1);
    border-radius: 0.75rem;
    color: #ededf2;
    font-size: 0.75rem;
    line-height: 1.25rem;
    word-break: break-all;
    box-sizing: border-box;
`;
const ButtonRow = styled.div `
    display: flex;
    gap: 0.5rem;
    width: 100%;
`;
const ActionButton = styled(Button) `
    flex: 1;
    gap: 0.5rem;
`;
const DoneButton = styled(Button) `
    width: 100%;
    margin-top: 0.5rem;
`;
const cameraInvitePopup = Popup.create(CameraInvitePopup);
export function showCameraInvitePopup({ anchorRef, onBeforeOpen, onDismiss, }) {
    executeSoon(async () => {
        onBeforeOpen?.();
        try {
            await cameraInvitePopup.pop({
                anchorRef,
            });
        }
        catch (_) {
        }
        finally {
            onDismiss?.();
        }
    });
}
